import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button, Dialog, Tabs, Tab } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { LogViewer, LogViewerSearch } from '@patternfly/react-log-viewer';
import { Toolbar, ToolbarContent, ToolbarItem } from '@patternfly/react-core';
import { gnbNodesService, GnbNode } from '../../services/gnbNodesService';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';

interface CiqPreviewDialogProps {
  node: GnbNode;
  onClose: () => void;
}

export default function CiqPreviewDialog({ node, onClose }: CiqPreviewDialogProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [contents, setContents] = useState<Record<string, string>>({});
  const fetchedRef = useRef<Set<string>>(new Set());
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const tabs: { key: string; label: string }[] = [];
  if (node.status === 'deployed') tabs.push({ key: 'active', label: 'Active CIQ' });
  else if (node.currentDraftName) tabs.push({ key: 'draft', label: 'In Draft CIQ' });
  if (node.hasStagedCiq) tabs.push({ key: 'staged', label: 'Staged CIQ' });
  if (node.hasStagedDay1) tabs.push({ key: 'day1', label: 'Day-1' });

  const fetchContent = async (key: string) => {
    if (fetchedRef.current.has(key)) return;
    fetchedRef.current.add(key);
    const fetchFn = key === 'active' || key === 'draft' ? gnbNodesService.previewCiq
      : key === 'staged' ? gnbNodesService.previewStagedCiq
      : gnbNodesService.previewStagedDay1;
    try {
      showLoader();
      const { data } = await fetchFn(node.id);
      setContents((prev) => ({ ...prev, [key]: data || 'No content available.' }));
      return;
    } catch (err) {
      fetchedRef.current.delete(key);
      setContents((prev) => ({ ...prev, [key]: 'Failed to fetch the file' }));
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to fetch ${key} CIQ: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    } finally {
      setTimeout(() => {
          hideLoader();
        }, 500);
    }
  };

  const handleDownload = async () => {
    const tab = tabs[activeTab];
    if (!tab) return;
    try {
      showLoader();
      const fetchFn = tab.key === 'active' || tab.key === 'draft' ? gnbNodesService.downloadCiq : tab.key === 'staged' ? gnbNodesService.downloadStagedCiq : gnbNodesService.downloadStagedDay1;
      const { data } = await fetchFn(node.id);
      const blob = data instanceof Blob ? data : new Blob([data as string], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${node.nodeName}-${tab.key}.yaml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to download file: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);

    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  };

  // Fetch first tab on mount
  useEffect(() => {
    if (tabs.length) fetchContent(tabs[0].key);
  }, []);

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}      
      title={`Preview - ${node.nodeName}`}
      show
      onClose={onClose}
      closeButtonLabel={'Close'}
      data-type="fullscreen" className="fullscreen"
      bottom={
        <>
          <ReactEDSWrapper component={Button} primary onClick={handleDownload}>Download</ReactEDSWrapper>
        </>
      }
    >
      
      <div style={{ width: '80vw', minWidth: '800px' }}>
      <ReactEDSWrapper component={Tabs} activeTab={activeTab} onTabChange={(_: unknown, idx: number) => { if (idx !== activeTab) { setActiveTab(idx); fetchContent(tabs[idx]?.key); } }}>
        {tabs.map((tab) => (
          <ReactEDSWrapper component={Tab} key={tab.key} title={tab.label}>
            <div style={{ height: '500px' }}>
              <LogViewer
                key={`${tab.key}-${contents[tab.key]?.length || 0}`}
                data={contents[tab.key] || 'Loading...'}
                hasLineNumbers
                isTextWrapped
                height="100%"
                width="100%"
                toolbar={
                  <Toolbar>
                    <ToolbarContent>
                      <ToolbarItem>
                        <LogViewerSearch placeholder="Search..." minSearchChars={1} />
                      </ToolbarItem>
                    </ToolbarContent>
                  </Toolbar>
                }
              />
            </div>
          </ReactEDSWrapper>
        ))}
      </ReactEDSWrapper>
      </div>
    </ReactEDSWrapper>,
    document.body
  );
}
